/* eslint-disable prettier/prettier */
import { Redirect, Route, Switch } from 'react-router-dom';

import authenticateVendor from '~/components/common/auth/AuthenticateVendor';
import withVendorStorefront from '~/components/common/vendors/WithVendorStorefront';
import useVendorUserContext from '~/hooks/vendors/useVendorUserContext';
import EXPERIMENT_FLAGS, { VENDOR_CREDITS_PAYMENT_INTENT } from '~/util/experimentFlags';
import featureFlags from '~/util/featureFlags';
import { loadable } from '~/util/routeUtils';

import ClientRouter from '../ClientRouter';

const Account = loadable(() => import('~/pages/vendors/Account/Account'));
const AccountPreAuth = loadable(() => import('~/pages/vendors/Account/PreAuth'));
const BecomeAVendorV2 = loadable(() => import('~/pages/vendors/BecomeAVendor/SubmitLeadV2'));
const CreditsAbout = loadable(() =>import('~/pages/vendors/Credits/AboutCredits'));
const CreditsCheckout = loadable(() =>import('~/pages/vendors/Credits/Checkout'));
const CreditsCheckoutV2 = loadable(() =>import('~/pages/vendors/CreditsCheckout'));
const CreditsCheckoutDeepLink = loadable(() =>import('~/pages/vendors/Credits/CheckoutDeepLink'));
const CreditsConnectionProtectionPolicy = loadable(() =>import('~/pages/vendors/Credits/ConnectionProtectionPolicy'));
const CreditsPlanManagement = loadable(() =>import('~/pages/vendors/Credits/PlanManagement'));
const CreditsPurchaseHistoryV2 = loadable(() =>import('~/pages/vendors/Credits/PurchaseHistory/PurchaseHistory'));
const CreditsSelectPlan = loadable(() =>import('~/pages/vendors/Credits/SelectPlan'));
const EditBadgesAndAwards = loadable(() =>import('~/pages/vendors/Storefront/editPages/BadgesAndAwards/BadgesAndAwards'));
const EditFAQ = loadable(() =>import('~/pages/vendors/Storefront/editPages/FAQ/EditFaqForm'));
const EditFAQs = loadable(() =>import('~/pages/vendors/Storefront/editPages/FAQ/FAQs'));
const EditMenuDetail = loadable(() =>import('~/pages/vendors/Storefront/editPages/MenuDetail'));
const EditMenus = loadable(() =>import('~/pages/vendors/Storefront/editPages/Menus'));
const EditPackageDetail = loadable(() =>import('~/pages/vendors/Storefront/editPages/Packages/components/PackageDetail'));
const EditPackages = loadable(() =>import('~/pages/vendors/Storefront/editPages/Packages/Packages'));
const EditPreferredVendorDetail = loadable(() =>import('~/pages/vendors/Storefront/editPages/PreferredVendorDetail'));
const EditPreferredVendors = loadable(() =>import('~/pages/vendors/Storefront/editPages/PreferredVendors'));
const EditRealWeddings = loadable(() =>import('~/pages/vendors/Storefront/editPages/RealWeddings'));
const EditRealWeddingsFlow = loadable(() =>import('~/pages/vendors/Storefront/editPages/RealWeddings/FlowComponent'));
const EditRealWeddingsSubmissionRules = loadable(() =>import('~/pages/vendors/Storefront/editPages/RealWeddings/SubmissionRules'));
const EditReviewDetail = loadable(() =>import('~/pages/vendors/Storefront/editPages/ReviewDetail'));
const EditReviewRequests = loadable(() =>import('~/pages/vendors/Storefront/editPages/ReviewRequests'));
const EditSpaceDetail = loadable(() =>import('~/pages/vendors/Storefront/editPages/Spaces/SpaceDetail'));
const EditSpaces = loadable(() =>import('~/pages/vendors/Storefront/editPages/Spaces/SpacesWrapper'));
const EditStories = loadable(() =>import('~/pages/vendors/Storefront/editPages/Stories'));
const EditStoryDetail = loadable(() =>import('~/pages/vendors/Storefront/editPages/StoryDetail'));
const EditVideoGallery = loadable(() =>import('~/pages/vendors/Storefront/editPages/VideoGallery'));
const FeaturedVendorPlacementSelectionView = loadable(() => import('~/pages/vendors/FeaturedVendorPlacements/SelectionView/SelectionView'));
const FeaturedVendorsCheckout = loadable(() => import('~/pages/vendors/FeaturedVendorPlacements/Checkout/Checkout'));
const FindCouples = loadable(() =>import('~/pages/vendors/FindCouples/FindCouples'));
const FindCouplesCouplesDetails = loadable(() =>import('~/pages/vendors/FindCouples/CouplesDetails'));
const FindCouplesFirstMoveDetails = loadable(() =>import('~/pages/vendors/FindCouples/FirstMoveDetails'));
const HowCreditsWork = loadable(() => import('~/pages/vendors/HowCreditsWork/HowCreditsWork'));
const HowWePrice = loadable(() =>import('~/pages/vendors/HowWePrice/HowWePrice'));
const InquiriesConnectView = loadable(() =>import('~/pages/vendors/Inquiries/ConnectView/ConnectView'));
const InquiriesDeclineView = loadable(() =>import('~/pages/vendors/Inquiries/DeclineView/DeclineView'));
const InquiriesInboxV2 = loadable(() =>import('~/pages/vendors/Inquiries/InboxV2/InboxV2'));
const InquiriesInboxV3 = loadable(() =>import('~/pages/vendors/Inquiries/InboxV3/InboxV3'));
const InquiriesInquiryConnectRedirect = loadable(() =>import('~/pages/vendors/Inquiries/InquiryConnectRedirect/InquiryConnectRedirect'));
const InquiriesInquiryDetail = loadable(() =>import('~/pages/vendors/Inquiries/InquiryDetail/InquiryDetail'));
const InquiriesInquiryView = loadable(() =>import('~/pages/vendors/Inquiries/InquiryView/InquiryView'));
const InquiriesPreAuthDeclineView = loadable(() =>import('~/pages/vendors/Inquiries/PreAuth/DeclineView'));
const InquiriesPreAuthRateLead = loadable(() =>import('~/pages/vendors/Inquiries/PreAuth/RateLead'));
const LeadPreferencesV2 = loadable(() =>import('~/pages/vendors/LeadPreferences/LeadPreferencesV2'));
const NoMatch = loadable(() => import('~/pages/NoMatch/NoMatch'));
const Onboard = loadable(() => import('~/pages/vendors/Onboard/Onboard'));
const OnboardV2 = loadable(() => import('~/pages/vendors/OnboardV2/components/Router'));
const Pledge = loadable(() => import('~/pages/vendors/Pledge/Pledge'));
const ReferAVendor = loadable(() =>import('~/pages/vendors/ReferAVendor/ReferAVendor'));
const VendorCommunity = loadable(() =>import('~/pages/vendors/Community/VendorCommunity'));
const VendorDashboard = loadable(() => import('~/pages/vendors/Dashboard/VendorDashboard'));
const VendorDashboardV2 = loadable(() =>import('~/pages/vendors/DashboardV2/VendorDashboardV2'));
const VendorLP = loadable(() => import('~/pages/vendors/vendorsLP/VendorsLP'));
const VendorsStorefrontSplitView = loadable(() =>import('~/pages/vendors/Storefront/VendorStorefrontSplitView'));
const WeddingsSubmissionsLP = loadable(() =>import('~/pages/vendors/weddings/SubmissionsLP/LandingPage'));

const VendorsRoutes = () => {
  const { vendorUserContext } = useVendorUserContext();
  const isInExperiment = EXPERIMENT_FLAGS.isUnderTest(
    VENDOR_CREDITS_PAYMENT_INTENT,
    vendorUserContext);
  return (
  <ClientRouter>
    <Switch>
      {/* Onboarding */}
      <Route exact path="/(inspiration|wedding-vendors)/become-a-vendor" component={BecomeAVendorV2} />
      <Route path="/inspire/vendors/onboard/venue" component={OnboardV2} />
      <Route path="/inspire/vendors/onboard/photographer" component={OnboardV2} />
      <Route path="/inspire/vendors/onboard/caterer" component={OnboardV2} />
      <Route path="/inspire/vendors/onboard/videographer" component={OnboardV2} />
      <Route path="/inspire/vendors/onboard/planner" component={OnboardV2} />
      <Route path="/inspire/vendors/onboard/bands-djs" component={OnboardV2} />
      <Route path="/inspire/vendors/onboard/florist" component={OnboardV2} />
      {featureFlags.get('enableBakersOnboarding') && (
        <Route path="/inspire/vendors/onboard/cakes-desserts" component={OnboardV2} />
      )}
      {featureFlags.get('enableBeauticiansOnboarding') && (
        <Route path="/inspire/vendors/onboard/hair-makeup" component={OnboardV2} />
      )}
      <Route path="/inspire/vendors/onboard/venue/(profile|details|pricing|review)" component={OnboardV2} />
      <Route path="/inspire/vendors/onboard/photographer/(profile|details|pricing|review)" component={OnboardV2} />
      <Route path="/inspire/vendors/onboard/caterer/(profile|details|pricing|review)" component={OnboardV2} />
      <Route path="/inspire/vendors/onboard/videographer/(profile|details|pricing|review)" component={OnboardV2} />
      <Route path="/inspire/vendors/onboard/planner/(profile|details|pricing|review)" component={OnboardV2} />
      <Route path="/inspire/vendors/onboard/bands-djs/(profile|details|pricing|review)" component={OnboardV2} />
      <Route path="/inspire/vendors/onboard/florist/(profile|details|pricing|review)" component={OnboardV2} />
      {featureFlags.get('enableBakersOnboarding') && (
        <Route path="/inspire/vendors/onboard/cakes-desserts/(profile|details|pricing|review)" component={OnboardV2} />
      )}
      {featureFlags.get('enableBeauticiansOnboarding') && (
        <Route path="/inspire/vendors/onboard/hair-makeup/(profile|details|pricing|review)" component={OnboardV2} />
      )}
      {/* @ts-expect-error Onboarding V1 components are not TypeScript */}
      <Route exact path="/inspire/vendors/onboard/" render={(props) => <Onboard {...props} key="preBoard" preBoard />} />
      {/* @ts-expect-error Onboarding V1 components are not TypeScript */}
      <Route exact path="/inspire/vendors/onboard/:type" render={(props) => <Onboard {...props} />} />

      {/* Edit */}
      <Route
        exact
        path={[
          '/inspire/vendors/listing/:editing(basic-information|pricing|cover-images|video|portfolio|about-venue|extra-costs|services|markets|bio|planning-levels-and-pricing)/:id?',
          '/inspire/vendors/listing',
        ]}
        component={authenticateVendor(VendorsStorefrontSplitView)}
      />
      <Route
        exact
        path="/inspire/vendors/listing/about-real-weddings"
        component={authenticateVendor(withVendorStorefront(WeddingsSubmissionsLP))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/rooms"
        component={authenticateVendor(withVendorStorefront(EditSpaces))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/rooms/:uuid"
        component={authenticateVendor(withVendorStorefront(EditSpaceDetail))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/sample-catered-weddings"
        component={authenticateVendor(withVendorStorefront(EditStories))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/sample-catered-weddings/:id"
        component={authenticateVendor(withVendorStorefront(EditStoryDetail))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/packages"
        component={authenticateVendor(withVendorStorefront(EditPackages))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/menus"
        component={authenticateVendor(withVendorStorefront(EditMenus))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/video-gallery"
        component={authenticateVendor(withVendorStorefront(EditVideoGallery))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/menus/:id"
        component={authenticateVendor(withVendorStorefront(EditMenuDetail))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/packages/:id"
        component={authenticateVendor(withVendorStorefront(EditPackageDetail))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/reviews"
        component={authenticateVendor(withVendorStorefront(EditReviewRequests))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/reviews/:uuid"
        component={authenticateVendor(withVendorStorefront(EditReviewDetail))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/faqs"
        component={authenticateVendor(withVendorStorefront(EditFAQs))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/faqs/:id"
        component={authenticateVendor(withVendorStorefront(EditFAQ))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/preferred-vendors"
        component={authenticateVendor(withVendorStorefront(EditPreferredVendors))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/preferred-vendors/:id"
        component={authenticateVendor(withVendorStorefront(EditPreferredVendorDetail))}
      />
      {/* The second parameter indicates that the storefront is required to be published  */}
      <Route
        exact
        path="/inspire/vendors/listing/badges"
        component={authenticateVendor(withVendorStorefront(EditBadgesAndAwards, true))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/real-weddings/:tab(drafts|submissions|tags)?"
        component={authenticateVendor(withVendorStorefront(EditRealWeddings))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/real-weddings/rules"
        component={authenticateVendor(withVendorStorefront(EditRealWeddingsSubmissionRules))}
      />
      <Route
        exact
        path="/inspire/vendors/listing/real-weddings/:slug(wedding-details|wedding-colors|wedding-vendors|upload-photos|terms-and-conditions)"
        component={authenticateVendor(withVendorStorefront(EditRealWeddingsFlow))}
      />

      {/* Inquiries */}
      <Route
        exact
        path="/inspire/vendors/find-couples/:slug(browse|invited)?"
        component={authenticateVendor(withVendorStorefront(FindCouples, true))}
      />
      <Route
        exact
        path="/inspire/vendors/find-couples/:uuid([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"
        component={authenticateVendor(withVendorStorefront(FindCouplesCouplesDetails, true))}
      />
      <Route
        exact
        path="/inspire/vendors/invited/:uuid([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"
        component={authenticateVendor(withVendorStorefront(FindCouplesFirstMoveDetails, true))}
      />
      {featureFlags.get('enableVendorInboxV3') && (
        <Route
          exact
          path="/inspire/vendors/leads/:status(ready|connected|booked|closed|find-couples/browse|find-couples/invited)/:uuid?"
          component={authenticateVendor(withVendorStorefront(InquiriesInboxV3))}
        />
      )}

      {!featureFlags.get('enableVendorInboxV3') && (
        <Route
          exact
          path="/inspire/vendors/leads/:status(ready|connected|booked|closed|find-couples/browse|find-couples/invited)"
          component={authenticateVendor(withVendorStorefront(InquiriesInboxV2))}
        />
      )}
      {!featureFlags.get('enableVendorInboxV3') && (
        <Route
          exact
          path="/inspire/vendors/leads/:status(ready|expired)/:uuid"
          component={authenticateVendor(withVendorStorefront(InquiriesInquiryDetail))}
        />
      )}
      {!featureFlags.get('enableVendorInboxV3') && (
          <Route
          exact
          path="/inspire/vendors/leads/:status(connected|booked|closed)/:uuid"
          component={authenticateVendor(withVendorStorefront(InquiriesInquiryView))}
        />
        )}
      <Route
        exact
        path="/inspire/vendors/connect/:uuid"
        component={authenticateVendor(withVendorStorefront(InquiriesConnectView))}
      />
      <Route
        exact
        path="/inspire/vendors/decline/:uuid"
        component={authenticateVendor(withVendorStorefront(InquiriesDeclineView))}
      />
      <Route
        exact
        path="/inspire/vendors/inquiry-connect/:uuid"
        component={authenticateVendor(withVendorStorefront(InquiriesInquiryConnectRedirect))}
      />
      <Route
        exact
        path="/inspire/vendors/email/decline/:token"
        component={InquiriesPreAuthDeclineView}
      />
      <Route
        exact
        path="/inspire/vendors/email/rate/:token/rating=:rating"
        component={InquiriesPreAuthRateLead}
      />

      {/* Credits */}
      <Route exact path="/inspire/vendors/how-plans-work" component={HowCreditsWork} />
      <Route
        exact
        path="/inspire/vendors/how-we-price"
        component={authenticateVendor(withVendorStorefront(HowWePrice))}
      />
      <Route
        exact
        path="/inspire/vendors/select-plan"
        component={authenticateVendor(withVendorStorefront(CreditsSelectPlan))}
      />
      <Route
        exact
        path="/inspire/vendors/about-plans"
        component={authenticateVendor(withVendorStorefront(CreditsAbout))}
      />
      <Route
        exact
        path="/inspire/vendors/connection-protection-policy"
        component={authenticateVendor(CreditsConnectionProtectionPolicy)}
      />
      <Route
        exact
        path="/inspire/vendors/purchase-history/credit-receipts"
      >
        <Redirect to="/inspire/vendors/purchase-history/purchases" />
       </Route>
       <Route
        exact
        path="/inspire/vendors/purchase-history/lead-receipts"
      >
        <Redirect to="/inspire/vendors/purchase-history/connections" />
       </Route>
      <Route
        exact
        path="/inspire/vendors/purchase-history"
        component={authenticateVendor(withVendorStorefront(CreditsPurchaseHistoryV2))}
      />
      <Route
        exact
        path="/inspire/vendors/purchase-history/:slug(purchases|connections|balance-history)"
        component={authenticateVendor(withVendorStorefront(CreditsPurchaseHistoryV2))}
      />
      <Route
        exact
        path="/inspire/vendors/checkout"
        component={authenticateVendor(withVendorStorefront(isInExperiment ? CreditsCheckoutV2 : CreditsCheckout))}
      />
      <Route
        exact
        path="/inspire/vendors/your-plans"
        component={authenticateVendor(withVendorStorefront(CreditsPlanManagement))}
      />
      <Route
        exact
        path="/inspire/vendors/email/storefront-promo/:promo"
        component={authenticateVendor(withVendorStorefront(CreditsCheckoutDeepLink))}
      />

      {/* Landing page, dashboard, lead preferences & community */}
      <Route exact path="/inspire/vendors/promo/:vendorPromoKey" component={VendorLP} />
      <Route exact path="/inspire/vendors/:auth(login|signup)" component={VendorLP} />
      <Route exact path="/inspire/vendors" component={VendorLP} />
        <Route exact path="/inspire/vendors/pledge" component={Pledge} />
        
        {featureFlags.get('vendorDashboardV2') && (
          <Route
            exact
            path="/inspire/vendors/dashboard"
            component={authenticateVendor(withVendorStorefront(VendorDashboardV2))}
          />
        )}
        {!featureFlags.get('vendorDashboardV2') && (
          <Route
            exact
            path="/inspire/vendors/dashboard"
            component={authenticateVendor(withVendorStorefront(VendorDashboard))}
          />
        )}
      <Route
        exact
        path="/inspire/vendors/lead-preferences"
        component={authenticateVendor(withVendorStorefront(LeadPreferencesV2))}
      />
      <Route
        exact
        path="/inspire/vendors/community"
        component={authenticateVendor(withVendorStorefront(VendorCommunity))}
      />
      <Route
        path="/inspire/vendors/account/unsubscribe/:userUuid"
        component={AccountPreAuth}
      />
      <Route
        exact
        path="/inspire/vendors/account"
        component={authenticateVendor(Account)}
      />
      <Route
        exact
        path="/inspire/vendors/refer-a-vendor"
        component={authenticateVendor(withVendorStorefront(ReferAVendor))}
      />
      <Route exact path="/inspire/referral/:referralCode([0-9A-Za-z]+)" component={VendorLP} />
      <Route
        exact
        path="/inspire/vendors/boost-listing"
        component={authenticateVendor(withVendorStorefront(FeaturedVendorPlacementSelectionView))}
      />
      <Route
        exact
        path="/inspire/vendors/boost-listing/checkout"
        component={authenticateVendor(withVendorStorefront(FeaturedVendorsCheckout))}
      />
      <Route component={NoMatch} />
    </Switch>
  </ClientRouter>
)};

export default VendorsRoutes;
